import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useProfile } from '../../context/ProfileContext';
import { useTheme } from '../../context/ThemeContext';
import { supabase } from '../../lib/supabase';
import CardImage from '../../components/CardImage';

// Icons
const ArrowLeftIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M19 12H5" />
    <path d="M12 19l-7-7 7-7" />
  </svg>
);

const CalendarIcon = ({ size = 48 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
    <line x1="16" y1="2" x2="16" y2="6" />
    <line x1="8" y1="2" x2="8" y2="6" />
    <line x1="3" y1="10" x2="21" y2="10" />
  </svg>
);

const MapPinIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
    <circle cx="12" cy="10" r="3" />
  </svg>
);

const ClockIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" />
    <path d="M12 6v6l4 2" />
  </svg>
);

const CheckCircleIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" />
    <path d="M9 12l2 2 4-4" />
  </svg>
);

const AlertCircleIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" />
    <line x1="12" y1="8" x2="12" y2="12" />
    <line x1="12" y1="16" x2="12.01" y2="16" />
  </svg>
);

const ChevronRightIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M9 18l6-6-6-6" />
  </svg>
);

const ChevronLeftIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M15 18l-6-6 6-6" />
  </svg>
);

const CloseIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M18 6L6 18" />
    <path d="M6 6l12 12" />
  </svg>
);

const SearchIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="11" cy="11" r="8" />
    <path d="m21 21-4.35-4.35" />
  </svg>
);

const CloseSmallIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M18 6L6 18" />
    <path d="M6 6l12 12" />
  </svg>
);

// Card Modal for viewing cards in scheduled trades
const CardModal = ({ cards, currentIndex, onClose, onNext, onPrev, partnerName }) => {
  const { theme, isDark } = useTheme();
  if (!cards || cards.length === 0) return null;

  const card = cards[currentIndex];
  const hasMultiple = cards.length > 1;

  return (
    <div style={{...cardModalStyles.overlay, backgroundColor: isDark ? 'rgba(0, 0, 0, 0.9)' : 'rgba(255, 255, 255, 0.9)'}} onClick={onClose}>
      <div style={cardModalStyles.content} onClick={(e) => e.stopPropagation()}>
        <button style={{...cardModalStyles.closeButton, backgroundColor: theme.border, color: theme.text}} onClick={onClose}>
          <CloseIcon />
        </button>

        <p style={{...cardModalStyles.headerText, color: theme.textSecondary}}>
          {hasMultiple
            ? `Cards in trade with ${partnerName}`
            : `Card in trade with ${partnerName}`
          }
        </p>

        <CardImage
          digitalImage={card.image || card.cardImage}
          name={card.name || card.cardName}
          theme={theme}
          style={cardModalStyles.cardImage}
        />

        <div style={cardModalStyles.cardInfo}>
          <h2 style={{...cardModalStyles.cardName, color: theme.text}}>{card.name || card.cardName}</h2>
          <p style={{...cardModalStyles.cardSubtitle, color: theme.textSecondary}}>
            {hasMultiple ? `${currentIndex + 1} of ${cards.length}` : 'Trade card'}
          </p>
        </div>

        {hasMultiple && (
          <div style={cardModalStyles.navRow}>
            <button
              style={{
                ...cardModalStyles.navButton,
                backgroundColor: theme.bgCard,
                border: `1px solid ${theme.border}`,
                color: theme.text,
                opacity: currentIndex === 0 ? 0.3 : 1,
              }}
              onClick={onPrev}
              disabled={currentIndex === 0}
            >
              <ChevronLeftIcon />
            </button>
            <button
              style={{
                ...cardModalStyles.navButton,
                backgroundColor: theme.bgCard,
                border: `1px solid ${theme.border}`,
                color: theme.text,
                opacity: currentIndex === cards.length - 1 ? 0.3 : 1,
              }}
              onClick={onNext}
              disabled={currentIndex === cards.length - 1}
            >
              <ChevronRightIcon />
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

const cardModalStyles = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 2000,
    padding: 20,
  },
  content: {
    position: 'relative',
    width: '100%',
    maxWidth: 340,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  closeButton: {
    position: 'absolute',
    top: -40,
    right: 0,
    width: 36,
    height: 36,
    borderRadius: '50%',
    border: 'none',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
  },
  headerText: {
    fontSize: 14,
    textAlign: 'center',
    marginBottom: 16,
  },
  cardImage: {
    width: '100%',
    maxWidth: 280,
    borderRadius: 12,
    boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
  },
  cardInfo: {
    textAlign: 'center',
    marginTop: 16,
  },
  cardName: {
    fontSize: 18,
    fontWeight: 600,
    margin: 0,
  },
  cardSubtitle: {
    fontSize: 13,
    marginTop: 4,
  },
  navRow: {
    display: 'flex',
    gap: 20,
    marginTop: 20,
  },
  navButton: {
    width: 48,
    height: 48,
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
  },
};

const StarIcon = ({ filled = false }) => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill={filled ? "#ffd700" : "none"} stroke="#ffd700" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
  </svg>
);

// Helper to get current user ID from users table
const getCurrentUserId = async () => {
  const { data } = await supabase.auth.getSession();
  const authId = data?.session?.user?.id;
  if (!authId) return null;

  const { data: userData, error } = await supabase
    .from('users')
    .select('id')
    .eq('auth_id', authId)
    .single();

  if (error || !userData) {
    console.error('Error fetching user ID from users table:', error);
    return null;
  }

  return userData.id;
};

// Fetch trades from Supabase (grouped by partner)
const fetchAllTrades = async (userId) => {
  try {
    // Fetch accepted trade requests that may or may not have schedules
    const { data: tradeRequests, error: requestsError } = await supabase
      .from('trade_requests')
      .select(`
        id,
        requester_id,
        card_owner_id,
        status,
        created_at,
        cards (name, digital_image_url),
        requester:users!trade_requests_requester_id_fkey (id, username),
        card_owner:users!trade_requests_card_owner_id_fkey (id, username)
      `)
      .or(`requester_id.eq.${userId},card_owner_id.eq.${userId}`)
      .in('status', ['accepted', 'completed']);

    if (requestsError) {
      console.error('Error fetching trade requests:', requestsError);
      return [];
    }

    if (!tradeRequests || tradeRequests.length === 0) {
      return [];
    }

    const tradeIds = tradeRequests.map(t => t.id);

    // Fetch schedules for these trades
    const { data: schedules, error: schedulesError } = await supabase
      .from('trade_schedules')
      .select(`
        id,
        trade_id,
        selected_date,
        selected_time,
        status,
        user1_confirmed,
        user2_confirmed,
        locations (
          store_name,
          address,
          city,
          state
        )
      `)
      .in('trade_id', tradeIds);

    if (schedulesError) {
      console.error('Error fetching schedules:', schedulesError);
    }

    // Create a map of schedules by trade_id
    const scheduleMap = {};
    (schedules || []).forEach(s => {
      scheduleMap[s.trade_id] = s;
    });

    // Fetch reviews to see if user has reviewed
    const { data: reviews } = await supabase
      .from('trade_reviews')
      .select('trade_schedule_id')
      .eq('reviewer_id', userId);

    const reviewedScheduleIds = new Set((reviews || []).map(r => r.trade_schedule_id));

    // Group by partner - multiple cards from same person = 1 trade
    const groupedByPartner = {};
    tradeRequests.forEach(request => {
      const isRequester = request.requester_id === userId;
      const partnerId = isRequester ? request.card_owner_id : request.requester_id;
      const partner = isRequester ? request.card_owner : request.requester;

      if (!groupedByPartner[partnerId]) {
        groupedByPartner[partnerId] = {
          requests: [],
          partner,
          isRequester,
        };
      }
      groupedByPartner[partnerId].requests.push(request);
    });

    // Transform grouped data
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    return Object.entries(groupedByPartner).map(([partnerId, group]) => {
      const { requests, partner } = group;
      const primaryRequest = requests[0];
      const allTradeIds = requests.map(r => r.id);

      const partnerName = partner?.username || 'Trader';

      // Collect all cards
      const cards = requests.map(r => ({
        name: r.cards?.name || 'Unknown Card',
        image: r.cards?.digital_image_url || '',
      }));

      // Check if ANY of the trades has a schedule (they should share one)
      let schedule = null;
      for (const req of requests) {
        if (scheduleMap[req.id]) {
          schedule = scheduleMap[req.id];
          break;
        }
      }

      const hasSchedule = schedule && schedule.selected_date && schedule.selected_time;
      const isCompleted = requests.every(r => r.status === 'completed');

      // Determine status
      let status = 'pending_schedule';
      if (isCompleted) {
        status = 'completed';
      } else if (hasSchedule) {
        status = 'scheduled';
      }

      // Calculate days until meetup
      let daysUntil = null;
      if (hasSchedule && !isCompleted) {
        const [month, day] = schedule.selected_date.split('/').map(Number);
        const year = today.getFullYear();
        const meetupDate = new Date(year, month - 1, day);
        meetupDate.setHours(0, 0, 0, 0);
        daysUntil = Math.ceil((meetupDate - today) / (1000 * 60 * 60 * 24));
      }

      // Check if needs review
      const needsReview = isCompleted && schedule && !reviewedScheduleIds.has(schedule.id);

      const location = schedule?.locations;
      const locationName = location?.store_name || (location?.city ? `${location.city}, ${location.state}` : null);
      const locationAddress = location?.address || null;

      // Card display
      const cardDisplay = cards.length > 1
        ? `${cards[0].name} +${cards.length - 1} more`
        : cards[0].name;

      return {
        id: primaryRequest.id,
        allTradeIds,
        status,
        partnerName,
        partnerAvatar: null,
        cardName: cardDisplay,
        cardImage: cards[0].image,
        cards, // All cards for display
        cardCount: cards.length,
        offeredCardName: null,
        offeredCardImage: null,
        scheduledDate: schedule?.selected_date || null,
        scheduledTime: schedule?.selected_time || null,
        location: locationName,
        locationAddress,
        user1Confirmed: schedule?.user1_confirmed || false,
        user2Confirmed: schedule?.user2_confirmed || false,
        daysUntil,
        rating: null,
        needsReview,
      };
    });
  } catch (error) {
    console.error('Error in fetchAllTrades:', error);
    return [];
  }
};

// Filter config
const FILTER_CONFIG = {
  scheduled: {
    title: 'Scheduled',
    emptyTitle: 'No scheduled meetups',
    emptySubtitle: 'When you schedule a trade meetup, it\'ll appear here',
  },
  pending: {
    title: 'Needs Scheduling',
    emptyTitle: 'No trades pending',
    emptySubtitle: 'Accepted trades that need a meetup scheduled',
  },
  completed: {
    title: 'Completed',
    emptyTitle: 'No completed trades',
    emptySubtitle: 'Your completed trades will appear here',
  },
};

const InfoIcon = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" />
    <line x1="12" y1="16" x2="12" y2="12" />
    <line x1="12" y1="8" x2="12.01" y2="8" />
  </svg>
);

// Trade Card Component
const TradeCard = ({ trade, onSchedule, onReview, onChat, onCardClick }) => {
  const { theme, isDark } = useTheme();
  const [showTips, setShowTips] = useState(false);
  const isScheduled = trade.status === 'scheduled';
  const isCompleted = trade.status === 'completed';
  const isPending = trade.status === 'pending_schedule';
  const bothConfirmed = trade.user1Confirmed && trade.user2Confirmed;

  // Get cards array for fan display
  const cards = trade.cards || [{ name: trade.cardName, image: trade.cardImage }];
  const hasMultiple = cards.length > 1;

  // Render fanned cards
  const renderCardFan = () => {
    const visibleCards = cards.slice(0, 3);
    return (
      <div style={styles.cardFanContainer}>
        {visibleCards.map((card, index) => (
          <CardImage
            key={index}
            digitalImage={card.image}
            name={card.name}
            theme={theme}
            style={{
              ...styles.tradeCardImage,
              ...styles.fannedCard,
              zIndex: 3 - index,
              transform: `translateX(${index * 10}px) rotate(${index * 4}deg)`,
            }}
          />
        ))}
        {cards.length > 3 && (
          <div style={{...styles.moreCardsIndicator, backgroundColor: theme.accent}}>
            <span style={{...styles.moreCardsText, color: isDark ? '#000000' : '#ffffff'}}>+{cards.length - 3}</span>
          </div>
        )}
      </div>
    );
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return null;
    // Handle "12/1" format (month/day)
    const [month, day] = dateStr.split('/').map(Number);
    if (!month || !day) return dateStr;
    const year = new Date().getFullYear();
    const date = new Date(year, month - 1, day);
    return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  };

  const getStatusInfo = () => {
    if (isCompleted) {
      return { text: 'Completed', color: theme.success, icon: <CheckCircleIcon /> };
    }
    if (isPending) {
      return { text: 'Needs Scheduling', color: '#ff8c66', icon: <AlertCircleIcon /> };
    }
    if (isScheduled && !bothConfirmed) {
      return { text: 'Awaiting Confirmation', color: theme.warning, icon: <AlertCircleIcon /> };
    }
    if (isScheduled && trade.daysUntil <= 1) {
      return { text: 'Tomorrow!', color: theme.success, icon: <CheckCircleIcon /> };
    }
    return { text: 'Confirmed', color: theme.success, icon: <CheckCircleIcon /> };
  };

  const statusInfo = getStatusInfo();

  return (
    <div style={{...styles.tradeCard, backgroundColor: theme.bgCard, borderColor: theme.border}}>
      {/* Review Badge */}
      {trade.needsReview && (
        <div style={{...styles.reviewBadge, backgroundColor: theme.warning}}>
          <span style={styles.reviewBadgeText}>Leave Review</span>
        </div>
      )}


      {/* Card Images - Fan if multiple - clickable */}
      <div
        style={{ ...styles.cardImagesRow, cursor: 'pointer' }}
        onClick={() => onCardClick && onCardClick(trade)}
      >
        {hasMultiple ? (
          renderCardFan()
        ) : (
          <div style={styles.tradeCardImageContainer}>
            <CardImage
              digitalImage={trade.cardImage}
              name={trade.cardName}
              theme={theme}
              style={styles.tradeCardImage}
            />
          </div>
        )}
      </div>

      {/* Trade Info */}
      <div style={styles.tradeInfo}>
        <div style={styles.tradeHeader}>
          <span style={{...styles.partnerName, color: theme.text}}>Trade with {trade.partnerName}</span>
          <div style={{ ...styles.statusChip, backgroundColor: `${statusInfo.color}20`, borderColor: statusInfo.color }}>
            <span style={{ ...styles.statusChipText, color: statusInfo.color }}>{statusInfo.text}</span>
          </div>
        </div>

        <span style={{...styles.cardNames, color: theme.accent}}>
          {trade.cardName}
          {trade.offeredCardName && ` for ${trade.offeredCardName}`}
        </span>

        {/* Schedule Info - Enhanced */}
        {isScheduled && trade.scheduledDate && (
          <div style={styles.scheduleInfo}>
            <div style={{...styles.scheduleRow, color: theme.textMuted}}>
              <CalendarIcon size={16} />
              <span style={styles.scheduleText}>{formatDate(trade.scheduledDate)}</span>
            </div>
            <div style={{...styles.scheduleRow, color: theme.textMuted}}>
              <ClockIcon />
              <span style={styles.scheduleText}>{trade.scheduledTime}</span>
            </div>
            <div style={{...styles.scheduleRow, color: theme.textMuted}}>
              <MapPinIcon />
              <div style={styles.locationDetails}>
                <span style={styles.scheduleText}>{trade.location}</span>
                {trade.locationAddress && (
                  <span style={{...styles.locationAddress, color: theme.textSecondary}}>{trade.locationAddress}</span>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Trade Tips - Expandable */}
        {showTips && isScheduled && bothConfirmed && (
          <div style={{
            ...styles.tipsSection,
            backgroundColor: isDark ? 'rgba(14, 165, 233, 0.1)' : 'rgba(14, 165, 233, 0.08)',
            borderColor: isDark ? 'rgba(14, 165, 233, 0.2)' : 'rgba(14, 165, 233, 0.15)',
          }}>
            <span style={{...styles.tipsTitle, color: theme.accent}}>What to Bring</span>
            <div style={styles.tipsList}>
              <div style={styles.tipItem}>
                <span style={{...styles.tipBullet, color: theme.accent}}>1.</span>
                <span style={{...styles.tipText, color: theme.text}}>All cards you want to trade</span>
              </div>
              <div style={styles.tipItem}>
                <span style={{...styles.tipBullet, color: theme.accent}}>2.</span>
                <span style={{...styles.tipText, color: theme.text}}>For graded cards: check ALT app for comps</span>
              </div>
              <div style={styles.tipItem}>
                <span style={{...styles.tipBullet, color: theme.accent}}>3.</span>
                <span style={{...styles.tipText, color: theme.text}}>For raw cards: check eBay, TCGPlayer</span>
              </div>
              <div style={styles.tipItem}>
                <span style={{...styles.tipBullet, color: theme.accent}}>4.</span>
                <span style={{...styles.tipText, color: theme.text}}>For rare cards: check recently sold listings</span>
              </div>
            </div>
          </div>
        )}

        {/* Completed Info */}
        {isCompleted && trade.rating && (
          <div style={styles.ratingRow}>
            <span style={{...styles.ratingLabel, color: theme.textMuted}}>Your rating:</span>
            <div style={styles.ratingStars}>
              {[...Array(5)].map((_, i) => (
                <StarIcon key={i} filled={i < trade.rating} />
              ))}
            </div>
          </div>
        )}

        {/* Pending Schedule CTA */}
        {isPending && (
          <button
            style={{
              ...styles.scheduleButton,
              backgroundColor: isDark ? 'rgba(14, 165, 233, 0.15)' : 'rgba(14, 165, 233, 0.1)',
              borderColor: theme.accent,
              color: theme.accent,
            }}
            onClick={() => onChat(trade)}
          >
            <CalendarIcon size={16} />
            <span>Open Chat to Schedule</span>
          </button>
        )}

        {/* Scheduled - Chat Button */}
        {isScheduled && !isCompleted && (
          <button
            style={{
              ...styles.chatButton,
              borderColor: theme.border,
              color: theme.textMuted,
            }}
            onClick={() => onChat(trade)}
          >
            <span>Open Chat</span>
          </button>
        )}

        {/* Needs Review CTA */}
        {trade.needsReview && (
          <button
            style={{
              ...styles.reviewButton,
              backgroundColor: isDark ? 'rgba(255, 193, 7, 0.15)' : 'rgba(255, 193, 7, 0.1)',
              borderColor: theme.warning,
              color: theme.warning,
            }}
            onClick={() => onReview(trade)}
          >
            <StarIcon />
            <span>Leave Review</span>
          </button>
        )}
      </div>

      {/* Info Button - vertically centered on right */}
      {isScheduled && bothConfirmed && (
        <button
          style={{
            ...styles.infoButton,
            backgroundColor: theme.border,
            borderColor: theme.border,
            color: theme.accent,
          }}
          onClick={() => setShowTips(!showTips)}
        >
          <InfoIcon size={18} />
        </button>
      )}
    </div>
  );
};

// Filter Tab Component
const FilterTab = ({ filter, isActive, count, onClick }) => {
  const { theme, isDark } = useTheme();
  return (
    <button
      style={{
        ...styles.filterTab,
        backgroundColor: isActive ? theme.accent : 'transparent',
        borderColor: isActive ? theme.accent : theme.border,
      }}
      onClick={onClick}
    >
      <span style={{
        ...styles.filterTabText,
        color: isActive ? (isDark ? '#000000' : '#ffffff') : theme.textMuted,
      }}>
        {FILTER_CONFIG[filter].title}
      </span>
      {count > 0 && (
        <span style={{
          ...styles.filterTabCount,
          backgroundColor: isActive ? (isDark ? 'rgba(0,0,0,0.2)' : 'rgba(255,255,255,0.2)') : theme.border,
          color: isActive ? (isDark ? '#000000' : '#ffffff') : theme.text,
        }}>
          {count}
        </span>
      )}
    </button>
  );
};

// Empty State Component
const EmptyState = ({ filter }) => {
  const { theme } = useTheme();
  const config = FILTER_CONFIG[filter];

  return (
    <div style={styles.emptyState}>
      <div style={{ color: theme.textSecondary }}>
        <CalendarIcon />
      </div>
      <span style={{...styles.emptyTitle, color: theme.text}}>{config.emptyTitle}</span>
      <span style={{...styles.emptySubtitle, color: theme.textMuted}}>{config.emptySubtitle}</span>
    </div>
  );
};

// Main Trades Component
const Trades = () => {
  const { theme, isDark } = useTheme();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const { activeProfile } = useProfile();

  const initialFilter = searchParams.get('filter') || 'scheduled';
  const shouldAutoFocusSearch = searchParams.get('search') === 'true';
  const [activeFilter, setActiveFilter] = useState(initialFilter);
  const [trades, setTrades] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchOpen, setSearchOpen] = useState(shouldAutoFocusSearch);

  // Card modal state
  const [selectedCards, setSelectedCards] = useState(null);
  const [selectedCardIndex, setSelectedCardIndex] = useState(0);
  const [selectedPartnerName, setSelectedPartnerName] = useState('');

  const loadTrades = async () => {
    // Use activeProfile.id (kid's ID when parent views as kid)
    if (!activeProfile?.id) {
      console.warn('No user ID found; cannot fetch trades');
      setTrades([]);
      setLoading(false);
      return;
    }

    const fetchedTrades = await fetchAllTrades(activeProfile.id);
    setTrades(fetchedTrades);
    setLoading(false);
  };

  useEffect(() => {
    setLoading(true);
    loadTrades();

    // Subscribe to real-time changes on trade_requests and trade_schedules
    const requestsSubscription = supabase
      .channel('public:trade_requests')
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'trade_requests' },
        () => loadTrades()
      )
      .subscribe();

    const schedulesSubscription = supabase
      .channel('public:trade_schedules')
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'trade_schedules' },
        () => loadTrades()
      )
      .subscribe();

    return () => {
      supabase.removeChannel(requestsSubscription);
      supabase.removeChannel(schedulesSubscription);
    };
  }, [user, activeProfile?.id]);

  // Filter trades based on active filter and search
  const getFilteredTrades = () => {
    let result;
    switch (activeFilter) {
      case 'scheduled':
        result = trades.filter(t => t.status === 'scheduled');
        break;
      case 'pending':
        result = trades.filter(t => t.status === 'pending_schedule');
        break;
      case 'completed':
        result = trades.filter(t => t.status === 'completed');
        break;
      default:
        result = trades;
    }

    // Apply search filter
    const query = searchQuery.toLowerCase().trim();
    if (query) {
      result = result.filter(t => {
        // Check partner name
        if (t.partnerName?.toLowerCase().includes(query)) return true;

        // Check location
        if (t.location?.toLowerCase().includes(query)) return true;

        // Check all cards in the fan (not just the display string)
        const cards = t.cards || [];
        const matchesAnyCard = cards.some(card =>
          card.name?.toLowerCase().includes(query)
        );
        if (matchesAnyCard) return true;

        // Check scheduled date (format: "12/1" or formatted "Sat, Dec 1")
        if (t.scheduledDate) {
          // Direct match on raw date
          if (t.scheduledDate.toLowerCase().includes(query)) return true;
          // Also check formatted version
          const [month, day] = t.scheduledDate.split('/').map(Number);
          if (month && day) {
            const year = new Date().getFullYear();
            const date = new Date(year, month - 1, day);
            const formatted = date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }).toLowerCase();
            if (formatted.includes(query)) return true;
          }
        }

        // Check scheduled time
        if (t.scheduledTime?.toLowerCase().includes(query)) return true;

        return false;
      });
    }

    return result;
  };

  const filteredTrades = getFilteredTrades();

  // Count per filter
  const counts = {
    scheduled: trades.filter(t => t.status === 'scheduled').length,
    pending: trades.filter(t => t.status === 'pending_schedule').length,
    completed: trades.filter(t => t.status === 'completed').length,
  };

  const handleBack = () => {
    navigate('/chat');
  };

  const handleChat = (trade) => {
    navigate(`/chat/${trade.id}`);
  };

  const handleReview = (trade) => {
    navigate(`/trade/${trade.id}/review`);
  };

  // Card modal handlers
  const handleCardClick = (trade) => {
    const cards = trade.cards || [];
    if (cards.length > 0) {
      setSelectedCards(cards);
      setSelectedCardIndex(0);
      setSelectedPartnerName(trade.partnerName);
    }
  };

  const handleCloseModal = () => {
    setSelectedCards(null);
    setSelectedCardIndex(0);
    setSelectedPartnerName('');
  };

  const handleNextCard = () => {
    if (selectedCards && selectedCardIndex < selectedCards.length - 1) {
      setSelectedCardIndex(selectedCardIndex + 1);
    }
  };

  const handlePrevCard = () => {
    if (selectedCards && selectedCardIndex > 0) {
      setSelectedCardIndex(selectedCardIndex - 1);
    }
  };

  return (
    <div style={{...styles.container, backgroundColor: theme.bg}}>
      {/* Header */}
      <div style={{...styles.header, backgroundColor: theme.bg, borderColor: theme.bgCard}}>
        <button style={{...styles.backButton, color: theme.accent}} onClick={handleBack}>
          <ArrowLeftIcon />
        </button>
        <div style={styles.headerContent}>
          {searchOpen ? (
            <div style={{...styles.searchInputContainer, backgroundColor: theme.bgCard, borderColor: theme.border}}>
              <SearchIcon />
              <input
                type="text"
                placeholder="Search by name, card, or location..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                style={{...styles.searchInput, color: theme.text}}
                autoFocus
              />
              {searchQuery && (
                <button
                  style={{...styles.clearSearchBtn, color: theme.textSecondary}}
                  onClick={() => setSearchQuery('')}
                >
                  <CloseSmallIcon />
                </button>
              )}
            </div>
          ) : (
            <>
              <h1 style={{...styles.headerTitle, color: theme.text}}>Your Trades</h1>
              <span style={{...styles.headerSubtitle, color: theme.textMuted}}>
                {filteredTrades.length} {FILTER_CONFIG[activeFilter].title.toLowerCase()}
              </span>
            </>
          )}
        </div>
        <button
          style={{...styles.searchToggleBtn, color: theme.accent}}
          onClick={() => {
            setSearchOpen(!searchOpen);
            if (searchOpen) setSearchQuery('');
          }}
        >
          {searchOpen ? <CloseSmallIcon /> : <SearchIcon />}
        </button>
      </div>

      {/* Filter Tabs */}
      <div style={styles.filterTabs}>
        {['scheduled', 'pending', 'completed'].map((filter) => (
          <FilterTab
            key={filter}
            filter={filter}
            isActive={activeFilter === filter}
            count={counts[filter]}
            onClick={() => setActiveFilter(filter)}
          />
        ))}
      </div>

      {/* Trades List */}
      <div style={styles.tradesList}>
        {loading ? (
          // Loading skeletons
          [...Array(2)].map((_, i) => (
            <div key={i} style={{...styles.skeletonCard, backgroundColor: theme.bgCard}}>
              <div style={styles.skeletonImageRow}>
                <div style={{...styles.skeletonImage, backgroundColor: theme.border}} />
                <div style={{...styles.skeletonImage, backgroundColor: theme.border}} />
              </div>
              <div style={styles.skeletonContent}>
                <div style={{...styles.skeletonLine, backgroundColor: theme.border}} />
                <div style={{ ...styles.skeletonLine, backgroundColor: theme.border, width: '60%' }} />
                <div style={{ ...styles.skeletonLine, backgroundColor: theme.border, width: '80%' }} />
              </div>
            </div>
          ))
        ) : filteredTrades.length > 0 ? (
          filteredTrades.map((trade) => (
            <TradeCard
              key={trade.id}
              trade={trade}
              onChat={handleChat}
              onReview={handleReview}
              onCardClick={handleCardClick}
            />
          ))
        ) : (
          <EmptyState filter={activeFilter} />
        )}
      </div>

      {/* Card Modal */}
      {selectedCards && (
        <CardModal
          cards={selectedCards}
          currentIndex={selectedCardIndex}
          onClose={handleCloseModal}
          onNext={handleNextCard}
          onPrev={handlePrevCard}
          partnerName={selectedPartnerName}
        />
      )}
    </div>
  );
};

// Styles
const styles = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
  },

  // Header
  header: {
    display: 'flex',
    alignItems: 'center',
    padding: '16px 20px',
    gap: 12,
    borderBottom: '1px solid',
  },
  backButton: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    padding: 4,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  headerContent: {
    flex: 1,
  },
  headerTitle: {
    fontSize: 20,
    fontWeight: 700,
    margin: 0,
  },
  headerSubtitle: {
    fontSize: 12,
  },
  searchInputContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    flex: 1,
    borderRadius: 8,
    padding: '8px 12px',
    border: '1px solid',
  },
  searchInput: {
    flex: 1,
    background: 'none',
    border: 'none',
    outline: 'none',
    fontSize: 14,
  },
  clearSearchBtn: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    padding: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  searchToggleBtn: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    padding: 8,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },

  // Filter Tabs
  filterTabs: {
    display: 'flex',
    gap: 8,
    padding: '12px 20px',
    overflowX: 'auto',
    scrollbarWidth: 'none',
    msOverflowStyle: 'none',
  },
  filterTab: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    padding: '8px 12px',
    borderRadius: 20,
    border: '1px solid',
    background: 'none',
    cursor: 'pointer',
    whiteSpace: 'nowrap',
    flexShrink: 0,
  },
  filterTabText: {
    fontSize: 12,
    fontWeight: 600,
  },
  filterTabCount: {
    fontSize: 10,
    fontWeight: 700,
    padding: '2px 6px',
    borderRadius: 10,
  },

  // Trades List
  tradesList: {
    flex: 1,
    overflowY: 'auto',
    padding: '0 20px 100px 20px',
  },
  tradeCard: {
    position: 'relative',
    display: 'flex',
    alignItems: 'flex-start',
    gap: 16,
    padding: 16,
    borderRadius: 12,
    border: '1px solid',
    marginBottom: 12,
    width: '100%',
    textAlign: 'left',
  },
  reviewBadge: {
    position: 'absolute',
    top: 8,
    right: 8,
    borderRadius: 4,
    padding: '2px 8px',
  },
  reviewBadgeText: {
    fontSize: 10,
    fontWeight: 700,
    color: '#000000',
  },
  infoButton: {
    width: 36,
    height: 36,
    borderRadius: 18,
    border: '1px solid',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    flexShrink: 0,
    alignSelf: 'center',
  },

  // Card Images
  cardImagesRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    flexShrink: 0,
  },
  tradeCardImageContainer: {
    position: 'relative',
  },
  tradeCardImage: {
    width: 50,
    height: 70,
    borderRadius: 4,
    objectFit: 'contain',
  },
  // Card Fan styles
  cardFanContainer: {
    position: 'relative',
    width: 70,
    height: 70,
    flexShrink: 0,
  },
  fannedCard: {
    position: 'absolute',
    top: 0,
    left: 0,
    transformOrigin: 'bottom left',
    boxShadow: '2px 2px 8px rgba(0,0,0,0.3)',
  },
  moreCardsIndicator: {
    position: 'absolute',
    bottom: 0,
    right: 0,
    borderRadius: 10,
    padding: '2px 6px',
    zIndex: 10,
  },
  moreCardsText: {
    fontSize: 9,
    fontWeight: 700,
  },
  swapArrow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  swapArrowText: {
    fontSize: 10,
    fontStyle: 'italic',
  },

  // Trade Info
  tradeInfo: {
    flex: 1,
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
  },
  tradeHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: 8,
  },
  partnerName: {
    fontSize: 14,
    fontWeight: 600,
  },
  statusChip: {
    padding: '4px 10px',
    borderRadius: 4,
    border: '1px solid',
    flexShrink: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  statusChipText: {
    fontSize: 10,
    fontWeight: 600,
    lineHeight: 1,
  },
  cardNames: {
    fontSize: 12,
    fontWeight: 500,
  },

  // Schedule Info
  scheduleInfo: {
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
    marginTop: 4,
  },
  scheduleRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  },
  scheduleText: {
    fontSize: 12,
  },
  locationDetails: {
    display: 'flex',
    flexDirection: 'column',
  },
  locationAddress: {
    fontSize: 11,
    marginTop: 2,
  },

  // Tips Section
  tipsSection: {
    marginTop: 12,
    padding: 12,
    borderRadius: 8,
    border: '1px solid',
  },
  tipsTitle: {
    fontSize: 12,
    fontWeight: 700,
    marginBottom: 8,
    display: 'block',
  },
  tipsList: {
    display: 'flex',
    flexDirection: 'column',
    gap: 6,
  },
  tipItem: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 8,
  },
  tipBullet: {
    fontSize: 11,
    fontWeight: 600,
    minWidth: 16,
  },
  tipText: {
    fontSize: 11,
    lineHeight: 1.4,
  },

  // Rating
  ratingRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    marginTop: 4,
  },
  ratingLabel: {
    fontSize: 11,
  },
  ratingStars: {
    display: 'flex',
    alignItems: 'center',
    gap: 2,
  },

  // CTAs
  scheduleButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    padding: '10px 16px',
    border: '1px solid',
    borderRadius: 8,
    fontSize: 12,
    fontWeight: 600,
    cursor: 'pointer',
    marginTop: 8,
  },
  reviewButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    padding: '10px 16px',
    border: '1px solid',
    borderRadius: 8,
    fontSize: 12,
    fontWeight: 600,
    cursor: 'pointer',
    marginTop: 8,
  },
  chatButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '8px 16px',
    backgroundColor: 'transparent',
    border: '1px solid',
    borderRadius: 8,
    fontSize: 12,
    fontWeight: 500,
    cursor: 'pointer',
    marginTop: 8,
  },

  // Empty State
  emptyState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 60,
    gap: 12,
  },
  emptyTitle: {
    fontSize: 16,
    fontWeight: 600,
  },
  emptySubtitle: {
    fontSize: 13,
    textAlign: 'center',
  },

  // Loading Skeletons
  skeletonCard: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 16,
    padding: 16,
    borderRadius: 12,
    marginBottom: 12,
  },
  skeletonImageRow: {
    display: 'flex',
    gap: 8,
  },
  skeletonImage: {
    width: 50,
    height: 70,
    borderRadius: 4,
    animation: 'pulse 1.5s ease-in-out infinite',
  },
  skeletonContent: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
  },
  skeletonLine: {
    height: 12,
    borderRadius: 4,
    animation: 'pulse 1.5s ease-in-out infinite',
  },
};

export default Trades;
